/**
 * Defines the {@link StandardCircular} class.
 * @module
 */
import React, { FC, ReactElement } from "react";
import "./StandardCircular.scss";
import { IconButton, ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { AddCircle, CheckCircle } from "@mui/icons-material";
import { convertCurrencyFromCents, formatToCurrency, safeInvoke } from "@utils";
import { CircularTypes } from "@componentTypes";

/**
 * Props interface for {@link StandardCircular}.
 */
interface Props {
    /**
     * The list of items to display for the circular.
     */
    items: CircularTypes.ItemDefinition[];
    /**
     * Tracks items added to list.
     */
    itemsInList: boolean[];
    /**
     * Tracks the window width.
     */
    windowWidth: number;
    /**
     * Triggered when an item's action button is clicked.
     * @param item - The item clicked.
     * @param index - The index of the item clicked.
     */
    onItemClicked?(item: CircularTypes.ItemDefinition, index: number): void;
}

/**
 * Generates the Standard Circular.
 * @param props - The component props.
 * @returns the StandardCircular component.
 */
export const StandardCircular: FC<Props> = (props: Props): ReactElement => {
    // Returned JSX.
    return (
        <ImageList
            classes={{
                root: "StandardCircular",
            }}
            rowHeight={220}
            cols={props.windowWidth > 800 ? 4 : props.windowWidth > 600 ? 3 : 2}
        >
            {props.items.map((item, idx) => {
                const displayedIcon = !props.itemsInList[idx] ? (
                    <IconButton
                        className="add-action-button"
                        aria-label={`Add ${item.title} to your list.`}
                        onClick={() => {
                            if (item.outOfNetworkActionUrl) {
                                // The out-of-network action URL will only be defined when
                                // the "plt" query string param has a value of "other".
                                window.open(item.outOfNetworkActionUrl, "_blank");
                            }

                            safeInvoke(props.onItemClicked, item, idx);
                        }}
                    >
                        <AddCircle />
                    </IconButton>
                ) : (
                    <IconButton
                        className="added-indicator"
                        aria-label={`Item ${item.title} has been added to your list.`}
                        disabled={true}
                    >
                        <CheckCircle />
                    </IconButton>
                );

                return (
                    <ImageListItem key={idx} cols={1}>
                        <img className="product-image" src={item.imageUrl} alt={item.title} draggable={false} />
                        <ImageListItemBar
                            className="header-tile-bar"
                            title={item.price ? formatToCurrency(convertCurrencyFromCents(item.price.amount)) : ""}
                            actionIcon={item.isWhileSuppliesLast ? <div>While supplies last!</div> : undefined}
                            position="top"
                        />
                        <ImageListItemBar
                            className="footer-tile-bar"
                            title={item.title}
                            subtitle={<div>{item.additionalDetails}</div>}
                            actionIcon={displayedIcon}
                        />
                    </ImageListItem>
                );
            })}
        </ImageList>
    );
};
